/**
 * VideoTemplateModal
 *
 * Modal to preview a video template with Canva edit action.
 * Simplified layout matching the graphics template modal.
 */
import React, { useRef, useEffect, useState } from 'react';
import { useAuth } from '../../../context/auth';
import { useToast } from '../../../components/ui/notifications/toast-notification.jsx';
import { useNavigate } from 'react-router-dom';
import { 
  X, 
  Play,
  Heart
} from 'lucide-react';
import './TemplateModal.css';

// Function to convert Firebase Storage gs:// URLs to HTTP URLs
const convertFirebaseStorageUrl = (gsUrl) => {
  if (!gsUrl) return gsUrl;
  
  // If it's already an HTTP URL (signed URL from backend), return as-is
  if (gsUrl.startsWith('http://') || gsUrl.startsWith('https://')) {
    return gsUrl;
  }
  
  // Only convert gs:// URLs (for backward compatibility)
  if (!gsUrl.startsWith('gs://')) {
    return gsUrl;
  }
  
  // Extract bucket and path from gs://bucket/path
  const match = gsUrl.match(/^gs:\/\/([^\/]+)\/(.+)$/);
  if (!match) {
    return gsUrl;
  }
  
  const [, bucket, path] = match;
  return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(path)}?alt=media`;
};

const VideoTemplateModal = ({ 
  videoTemplate, 
  isOpen, 
  onClose, 
  isSaved = false,
  onSaveVideoTemplate,
  isLoggedIn = false,
  userPlan = 'basic'
}) => {
  const modalRef = useRef(null);
  const videoRef = useRef(null);
  const canvaButtonRef = useRef(null);
  const [isEditingInCanva, setIsEditingInCanva] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast?.() || { addToast: (msg) => console.log(msg) };

  // Auto-play video when modal opens (muted)
  useEffect(() => {
    if (isOpen && videoRef.current && videoTemplate?.videoUrl) {
      setVideoLoading(true);
      videoRef.current.muted = true;
      videoRef.current.currentTime = 0;
      setVideoError(false);
    }
  }, [isOpen, videoTemplate]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen) return;
      
      switch (event.key) {
        case 'Escape':
          onClose();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Handle clicks outside the modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Add event listener for the Canva button
  useEffect(() => {
    const buttonElement = canvaButtonRef.current;
    
    if (buttonElement) {
      const handleMouseEnter = () => {
        buttonElement.style.background = "linear-gradient(45deg, #00D4DC, #8D3AF8)";
      };
      
      const handleMouseLeave = () => {
        buttonElement.style.background = "linear-gradient(45deg, #00C4CC, #7D2AE8)";
      };
      
      buttonElement.addEventListener('mouseenter', handleMouseEnter);
      buttonElement.addEventListener('mouseleave', handleMouseLeave);
      
      return () => {
        buttonElement.removeEventListener('mouseenter', handleMouseEnter);
        buttonElement.removeEventListener('mouseleave', handleMouseLeave);
      };
    }
  }, [isOpen]);

  const handleEditInCanva = async () => {
    if (!videoTemplate?.canvaLink || isEditingInCanva) return;

    // If user is on basic plan, redirect to pricing page
    if (userPlan === 'basic' || (user && (user.planLevel === 'basic' || user.subscriptionStatus?.currentPlanLevel === 'basic'))) {
      onClose();
      navigate('/app/account', { state: { fromTemplates: true } });
      return;
    }

    setIsEditingInCanva(true);

    try {
      // Track video template usage
      if (videoTemplate?.id) {
        const { useVideoTemplate } = await import('../../../api/content/videoTemplatesApi');
        useVideoTemplate(videoTemplate.id).catch(err => {
          console.error('Failed to record video template usage:', err);
        });
      }

      // Open Canva link in new tab
      window.open(videoTemplate.canvaLink, '_blank', 'noopener,noreferrer');
      addToast('Opening template in Canva...', 'success');
    } catch (error) {
      console.error('Edit in Canva failed:', error);
      addToast('Failed to open Canva. Please try again.', 'error');
    } finally {
      setTimeout(() => {
        setIsEditingInCanva(false);
      }, 2000);
    }
  };

  // Handle save/unsave video template
  const handleSaveClick = () => {
    if (onSaveVideoTemplate) {
      onSaveVideoTemplate(videoTemplate.id, !isSaved);
    }
  };

  if (!isOpen || !videoTemplate) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 md:p-4 bg-black/60 backdrop-blur-sm">
      <div 
        className="animate-in fade-in zoom-in-95 duration-200 ease-out"
        style={{ animationFillMode: 'forwards' }}
      >
        <div 
          ref={modalRef}
          className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden"
        >
          {/* Close button - positioned in corner */}
          <button 
            onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-full bg-white/90 dark:bg-gray-800/90 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors z-20 shadow-md"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          </button>

          {/* Save button - positioned in corner */}
          {isLoggedIn && onSaveVideoTemplate && (
            <button
              onClick={handleSaveClick}
              className={`absolute top-3 left-3 p-2 rounded-full z-20 shadow-md ${
                isSaved 
                  ? 'bg-gradient-to-r from-purple-500 to-purple-600' 
                  : 'bg-white/90 dark:bg-gray-800/90 hover:bg-gray-100 dark:hover:bg-gray-700'
              } transition-colors`}
              aria-label={isSaved ? "Unsave video template" : "Save video template"}
            >
              <Heart 
                className={`w-4 h-4 ${isSaved ? 'text-white fill-white' : 'text-gray-600 dark:text-gray-300'}`} 
              />
            </button>
          )}

          {/* Main Content */}
          <div className="p-6 pt-14 md:p-8 md:pt-14">
            {/* Inline-flex container ensures button matches video width */}
            <div className="inline-flex flex-col">
              {/* Video player */}
              {videoTemplate.videoUrl && !videoError ? (
                <div className="relative">
                  {/* Video Skeleton - shown while loading */}
                  {videoLoading && (
                    <div 
                      className="bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"
                      style={{
                        width: 'min(80vw, 600px)',
                        aspectRatio: '16/9'
                      }}
                    />
                  )}
                  
                  <video 
                    ref={videoRef}
                    src={convertFirebaseStorageUrl(videoTemplate.videoUrl)}
                    controls
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="rounded-lg shadow-md"
                    style={{ 
                      display: videoLoading ? 'none' : 'block',
                      maxWidth: 'min(80vw, 600px)',
                      maxHeight: '60vh',
                      width: 'auto',
                      height: 'auto'
                    }}
                    onError={() => {
                      setVideoError(true);
                      setVideoLoading(false);
                    }}
                    onLoadedData={() => setVideoLoading(false)}
                    onCanPlay={() => setVideoLoading(false)}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              ) : (
                <div 
                  className="bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center shadow-md"
                  style={{
                    width: 'min(80vw, 600px)',
                    aspectRatio: '16/9'
                  }}
                >
                  <div className="text-center">
                    <Play className="w-16 h-16 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">Video unavailable</p>
                  </div>
                </div>
              )}

              {/* Edit in Canva button - matches video width */}
              {videoTemplate.canvaLink && (
                <button
                  ref={canvaButtonRef}
                  onClick={handleEditInCanva}
                  disabled={isEditingInCanva || videoLoading}
                  className="w-full mt-5 py-3.5 px-6 rounded-lg text-base font-semibold text-white transition-all flex items-center justify-center shadow-md hover:shadow-lg"
                  style={{
                    background: videoLoading 
                      ? "#9CA3AF"
                      : "linear-gradient(45deg, #00C4CC, #7D2AE8)",
                    cursor: (isEditingInCanva || videoLoading) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isEditingInCanva ? 'Opening Canva...' : videoLoading ? 'Loading...' : 'Edit in Canva'}
                </button>
              )}

              {/* Close button - only visible on mobile */}
              <button
                onClick={onClose}
                className="w-full mt-3 py-2.5 px-3 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors lg:hidden"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoTemplateModal;
